import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactImageGallery from 'react-image-gallery';

import { propTypes } from '../../../util/types';
import {
  FormattedMessage,
  injectIntl,
  intlShape,
} from '../../../util/reactIntl';
import {
  AspectRatioWrapper,
  Button,
  IconClose,
  IconArrowHead,
  ResponsiveImage,
} from '../../../components';

// Copied directly from
// `node_modules/react-image-gallery/styles/css/image-gallery.css`. The
// copied file is left unedited, and all the overrides are defined in
// the component CSS file below.
import './image-gallery.css';

import css from './ListingImageGallery.module.css';

const IMAGE_GALLERY_OPTIONS = {
  showPlayButton: false,
  disableThumbnailScroll: true,
  slideDuration: 350,
  lazyLoad: true,
};
const MAX_LANDSCAPE_ASPECT_RATIO = 2; // 2:1
const MIN_PORTRAIT_ASPECT_RATIO = 3 / 4;

const getFirstImageAspectRatio = (firstImage, scaledVariant) => {
  if (!firstImage) {
    return { aspectWidth: 1, aspectHeight: 1 };
  }

  const v = firstImage?.attributes?.variants?.[scaledVariant];
  const w = v?.width;
  const h = v?.height;
  const hasDimensions = !!w && !!h;
  const aspectRatio = w / h;

  // We keep the fractions separated as these are given to AspectRatioWrapper
  // which expects separate width and height
  return hasDimensions && aspectRatio >= MAX_LANDSCAPE_ASPECT_RATIO
    ? { aspectWidth: 2, aspectHeight: 1 }
    : hasDimensions && aspectRatio <= MIN_PORTRAIT_ASPECT_RATIO
    ? { aspectWidth: 3, aspectHeight: 4 }
    : hasDimensions
    ? { aspectWidth: w, aspectHeight: h }
    : { aspectWidth: 1, aspectHeight: 1 };
};

const ListingImageGallery = props => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryRef, setGalleryRef] = useState(null);
  const {
    intl,
    rootClassName,
    className,
    images,
    imageVariants,
    thumbnailVariants,
  } = props;

  // Add defensive check for images
  if (!images || images.length === 0) {
    return <div className={css.loadingPlaceholder}>No images available</div>;
  }

  const thumbVariants = thumbnailVariants || imageVariants;

  // imageVariants are scaled variants.
  const { aspectWidth, aspectHeight } = getFirstImageAspectRatio(
    images?.[0],
    imageVariants[0]
  );
  const items = images.map((img, i) => {
    // Get the best available image variant for the original
    const originalVariant =
      img.attributes?.variants?.['scaled-xlarge'] ||
      img.attributes?.variants?.['scaled-large'] ||
      img.attributes?.variants?.['scaled-medium'] ||
      img.attributes?.variants?.[imageVariants[0]];

    return {
      // react-image-gallery requires the `original` key with a valid URL
      original: originalVariant?.url || '',
      index: i,
      alt: intl.formatMessage(
        { id: 'ListingImageGallery.imageAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbAlt: intl.formatMessage(
        { id: 'ListingImageGallery.imageThumbnailAltText' },
        { index: i + 1, count: images.length }
      ),
      thumbnail: img.attributes?.variants?.[thumbVariants[0]]?.url,
      image: img,
    };
  });

  const imageSizesMaybe = isFullscreen
    ? {}
    : {
        sizes: `(max-width: 1024px) 100vw, (max-width: 1200px) calc(100vw - 192px), 708px`,
      };
  const renderItem = item => {
    return (
      <>
        <AspectRatioWrapper
          width={aspectWidth || 1}
          height={aspectHeight || 1}
          className={isFullscreen ? css.itemWrapperFullscreen : css.itemWrapper}
        >
          <div
            className={`${css.itemCentering} ${
              !isFullscreen ? css.magnifyable : ''
            }`}
            onClick={() => {
              if (!isFullscreen && galleryRef) {
                galleryRef.toggleFullScreen();
              }
            }}
          >
            <ResponsiveImage
              rootClassName={isFullscreen ? css.itemFullscreen : css.item}
              image={item.image}
              alt={item.alt}
              variants={imageVariants}
              {...imageSizesMaybe}
            />
          </div>
        </AspectRatioWrapper>
        <div className={css.itemCaption}>
          The cover image is for representation and may differ from the actual
          product.
        </div>
      </>
    );
  };
  const renderThumbInner = item => {
    return (
      <div style={{ width: 88, height: 88, overflow: 'hidden' }}>
        <ResponsiveImage
          rootClassName={css.thumb}
          image={item.image}
          alt={item.thumbAlt}
          variants={thumbVariants}
          sizes="88px"
        />
      </div>
    );
  };

  const onScreenChange = isFull => {
    setIsFullscreen(isFull);
  };

  const onSlide = index => {
    setCurrentIndex(index);
  };

  const renderDotIndicators = () => {
    return (
      <div className={css.dotIndicators}>
        {images.map((_, index) => (
          <button
            key={index}
            className={`${css.dot} ${
              index === currentIndex ? css.activeDot : ''
            }`}
            onClick={() => {
              if (galleryRef) {
                galleryRef.slideToIndex(index);
              }
            }}
            aria-label={intl.formatMessage(
              { id: 'ListingImageGallery.goToSlide' },
              { index: index + 1 }
            )}
          />
        ))}
      </div>
    );
  };

  const renderLeftNav = (onClick, disabled) => {
    return (
      <button className={css.navLeft} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="left" size="big" />
        </div>
      </button>
    );
  };
  const renderRightNav = (onClick, disabled) => {
    return (
      <button className={css.navRight} disabled={disabled} onClick={onClick}>
        <div className={css.navArrowWrapper}>
          <IconArrowHead direction="right" size="big" />
        </div>
      </button>
    );
  };
  const renderFullscreenButton = (onClick, isFullscreen) => {
    return isFullscreen ? (
      <Button
        onClick={onClick}
        rootClassName={css.close}
        title={intl.formatMessage({
          id: 'ListingImageGallery.closeModalTitle',
        })}
      >
        <span className={css.closeText}>
          <FormattedMessage id="ListingImageGallery.closeModal" />
        </span>
        <IconClose rootClassName={css.closeIcon} />
      </Button>
    ) : null;
  };

  if (items.length === 0) {
    return (
      <ResponsiveImage
        className={css.noImage}
        image={null}
        variants={[]}
        alt=""
      />
    );
  }

  const classes = classNames(rootClassName || css.root, className);

  return (
    <>
      <ReactImageGallery
        ref={setGalleryRef}
        additionalClass={classes}
        items={items}
        renderItem={renderItem}
        renderThumbInner={renderThumbInner}
        onScreenChange={onScreenChange}
        onSlide={onSlide}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        renderFullscreenButton={renderFullscreenButton}
        showThumbnails={images.length > 1}
        {...IMAGE_GALLERY_OPTIONS}
      />
      {!isFullscreen && images.length > 1 && renderDotIndicators()}
    </>
  );
};

ListingImageGallery.defaultProps = {
  rootClassName: null,
  className: null,
  thumbnailVariants: null,
};

const { string, arrayOf } = PropTypes;

ListingImageGallery.propTypes = {
  rootClassName: string,
  className: string,
  images: arrayOf(propTypes.image).isRequired,
  imageVariants: arrayOf(string).isRequired,
  thumbnailVariants: arrayOf(string),

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(ListingImageGallery);
